import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldExclamationIcon,
  UserGroupIcon,
  NoSymbolIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ClockIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import AdminLayout from '../components/AdminLayout';
import StatsCard from '../components/StatsCard';
import useApi from '../hooks/useApi';

// Risk level badge component
const RiskLevelBadge = ({ level }) => {
  const colors = {
    low: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800'
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[level] || colors.low}`}>
      {level?.charAt(0).toUpperCase() + level?.slice(1) || 'Unknown'}
    </span>
  );
};

// Risk score indicator
const RiskScoreIndicator = ({ score }) => {
  const getColor = (s) => {
    if (s >= 80) return 'bg-red-500';
    if (s >= 60) return 'bg-orange-500';
    if (s >= 30) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${getColor(score)} rounded-full`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className="text-sm font-medium text-gray-700">{score}</span>
    </div>
  );
};

const RiskDashboard = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const { data, loading, error, refetch } = useApi('/api/admin/risk/dashboard', [refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    refetch();
  };

  const stats = data?.data?.stats || {};
  const recentHighRiskSignups = data?.data?.recentHighRiskSignups || [];
  const recentBlockedAttempts = data?.data?.recentBlockedAttempts || [];
  const blockedEntitiesByType = data?.data?.blockedEntitiesByType || [];
  const riskDistribution = data?.data?.riskDistribution || [];

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Risk Management</h1>
            <p className="text-gray-600 mt-1">Monitor and manage security risks</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/risk/users"
              className="px-4 py-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
            >
              View All Users
            </Link>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            Failed to load risk dashboard: {error.message || 'Unknown error'}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Total Profiles"
            value={stats.totalProfiles || 0}
            icon={UserGroupIcon}
            loading={loading}
          />
          <StatsCard
            title="Flagged Users"
            value={stats.flaggedUsers || 0}
            icon={ExclamationTriangleIcon}
            loading={loading}
            trend={stats.flaggedUsers > 0 ? 'warning' : null}
          />
          <StatsCard
            title="Blocked Users"
            value={stats.blockedUsers || 0}
            icon={NoSymbolIcon}
            loading={loading}
            trend={stats.blockedUsers > 0 ? 'danger' : null}
          />
          <StatsCard
            title="High Risk Users"
            value={(stats.highRiskUsers || 0) + (stats.criticalRiskUsers || 0)}
            icon={ShieldExclamationIcon}
            loading={loading}
            trend={(stats.highRiskUsers + stats.criticalRiskUsers) > 0 ? 'warning' : null}
          />
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Captcha Required</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.captchaRequired || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">OTP Required (Silent)</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.otpRequired || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Avg Risk Score</h3>
            <p className="text-2xl font-bold text-gray-900">
              {(stats.averageRiskScore || 0).toFixed(1)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Risk Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5 text-gray-400" />
              Risk Score Distribution
            </h2>
            <div className="space-y-3">
              {riskDistribution.length > 0 ? (
                riskDistribution.map((range) => (
                  <div key={range.score_range} className="flex items-center gap-3">
                    <span className="w-16 text-sm text-gray-600">{range.score_range}</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          range.score_range.startsWith('9') ? 'bg-red-500' :
                          range.score_range.startsWith('8') || range.score_range.startsWith('7') ? 'bg-orange-500' :
                          range.score_range.startsWith('6') || range.score_range.startsWith('5') || range.score_range.startsWith('4') ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${Math.min((range.count / (stats.totalProfiles || 1)) * 100 * 5, 100)}%` }}
                      />
                    </div>
                    <span className="w-12 text-right text-sm font-medium text-gray-700">{range.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No data available</p>
              )}
            </div>
          </div>

          {/* Blocked Entities by Type */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <NoSymbolIcon className="w-5 h-5 text-gray-400" />
              Blocked Entities
            </h2>
            <div className="space-y-3">
              {blockedEntitiesByType.length > 0 ? (
                blockedEntitiesByType.map((entity) => (
                  <div key={entity.entity_type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium text-gray-900 capitalize">
                        {entity.entity_type.replace(/_/g, ' ')}
                      </span>
                      <span className="ml-2 text-sm text-gray-500">
                        ({entity.count} blocked)
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {entity.total_hits || 0} hits
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No blocked entities</p>
              )}
              <Link
                to="/risk/blocked"
                className="block text-center text-sm text-primary-600 hover:text-primary-700 pt-2"
              >
                Manage Blocked Entities →
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent High-Risk Signups */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />
              High-Risk Signups (Last 24h)
            </h2>
            <div className="space-y-3">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                </div>
              ) : recentHighRiskSignups.length > 0 ? (
                recentHighRiskSignups.map((user) => (
                  <Link
                    key={user.user_id}
                    to={`/risk/users/${user.user_id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        {user.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{user.email || 'No email'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <RiskScoreIndicator score={user.risk_score} />
                      <RiskLevelBadge level={user.risk_level} />
                    </div>
                  </Link>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No high-risk signups in the last 24 hours</p>
              )}
            </div>
          </div>

          {/* Recent Blocked Attempts */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ClockIcon className="w-5 h-5 text-red-500" />
              Recent Blocked Attempts
            </h2>
            <div className="space-y-3">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                </div>
              ) : recentBlockedAttempts.length > 0 ? (
                recentBlockedAttempts.map((action) => (
                  <div
                    key={action.id}
                    className="p-3 bg-red-50 rounded-lg border border-red-100"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">
                        {action.email || action.name || 'Unknown User'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(action.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-red-700">{action.action_reason}</p>
                    {action.ip_address && (
                      <p className="text-xs text-gray-500 mt-1">IP: {action.ip_address}</p>
                    )}
                    {action.user_id && (
                      <Link
                        to={`/risk/users/${action.user_id}`}
                        className="text-xs text-primary-600 hover:text-primary-700 mt-1 inline-block"
                      >
                        View User →
                      </Link>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No blocked attempts recently</p>
              )}
              <Link
                to="/risk/audit"
                className="block text-center text-sm text-primary-600 hover:text-primary-700 pt-2"
              >
                View Full Audit Trail →
              </Link>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/risk/users?isFlagged=true"
              className="px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors"
            >
              <ExclamationTriangleIcon className="w-4 h-4 inline mr-2" />
              View Flagged Users ({stats.flaggedUsers || 0})
            </Link>
            <Link
              to="/risk/users?isBlocked=true"
              className="px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
            >
              <NoSymbolIcon className="w-4 h-4 inline mr-2" />
              View Blocked Users ({stats.blockedUsers || 0})
            </Link>
            <Link
              to="/risk/users?minScore=60"
              className="px-4 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors"
            >
              <ShieldExclamationIcon className="w-4 h-4 inline mr-2" />
              High Risk Users (Score 60+)
            </Link>
            <Link
              to="/risk/blocked"
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <NoSymbolIcon className="w-4 h-4 inline mr-2" />
              Manage Blocked Entities
            </Link>
            <Link
              to="/risk/audit"
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <EyeIcon className="w-4 h-4 inline mr-2" />
              View Audit Trail
            </Link>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default RiskDashboard;
